import "@xyflow/react/dist/style.css";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ContextGraphCanvas from "./ContextGraphCanvas";
import ContextGraphLegend from "./ContextGraphLegend";
import ContextGraphNodePanel from "./ContextGraphNodePanel";
import ContextGraphScopeControls from "./ContextGraphScopeControls";
import ContextGraphScopeToggle, { type AnswerScope } from "./ContextGraphScopeToggle";
import { collapseGraph, collapsedIds, companiesIn, isCollapsedNode } from "./collapse";
import "./contextGraph.css";
import ErrorDisplay from "../ErrorDisplay";
import {
	contextGraphService,
	type ContextGraphResult,
} from "../../services/contextGraphService";
import type { ContextGraph as ContextGraphData, ContextNode } from "../../types/contextGraph";

type LoadState =
	| { phase: "loading" }
	| { phase: "ready"; graph: ContextGraphData }
	| { phase: "unavailable" }
	| { phase: "error" };

/**
 * Below this the node detail cannot sit beside the canvas and stay readable:
 * the panel wants ~360px and the graph wants the rest. The Analyst rail opens
 * at 440px, so the rail is always under it and the page always over it.
 */
const SIDE_BY_SIDE_MIN_WIDTH = 720;

export interface ContextGraphAnswerViewProps {
	/** The chat's public id. */
	chatId: string;
	/**
	 * The DATABASE message id (the digits of the chat's `history-<id>`). Empty
	 * for a chat with no one answer to narrow to — the conversation reading is
	 * then the only one, and "This answer" is offered disabled.
	 */
	messageId: string;
	/**
	 * Controlled scope, for a host that keeps it in the URL (the page).
	 * Omit both and the view keeps its own, starting at "message".
	 */
	scope?: AnswerScope;
	onScopeChange?: (scope: AnswerScope) => void;
	/**
	 * Controlled company focus, for the same reason: the page keeps it in
	 * `?focus=` so a neighbourhood is shareable. `undefined` means the reader
	 * has not chosen — the graph's own focus is used. `null` is the reader
	 * explicitly asking for the whole answer. Controlled iff `onFocusChange`
	 * is given; otherwise the view keeps its own.
	 */
	focus?: string | null;
	onFocusChange?: (focus: string | null) => void;
	/** Opens a citation's `doppler://` href (the host's `useDopplerUriOpener`). */
	onOpenHref: (href: string, filename?: string) => void;
}

/**
 * The graph behind one answer, as a component a host can mount anywhere —
 * the context-graph page for its chat/message scopes, and the Analyst rail
 * in place of the thread (DOP-493).
 *
 * CONTRACT (agreed 2026-09-09, DOP-492/493): the host owns the chrome around
 * it (a back control, a title, the padding it sits in); this view owns
 * everything inside — loading the graph, the scope toolbar (This answer /
 * Conversation, focus, expand), the canvas, the legend, and the selected
 * node's detail panel.
 *
 * The one thing that is not the same in both hosts is where the node detail
 * goes: beside the canvas where there is room for both, and below it as a
 * bottom sheet where there is not. That is measured from the view's own
 * width with a ResizeObserver rather than a media query, because the rail is
 * a resizable column inside a full-width window — the viewport says nothing
 * about how much room this view actually has.
 */
const ContextGraphAnswerView: React.FC<ContextGraphAnswerViewProps> = ({
	chatId,
	messageId,
	scope,
	onScopeChange,
	focus,
	onFocusChange,
	onOpenHref,
}) => {
	const [state, setState] = useState<LoadState>({ phase: "loading" });
	const [selected, setSelected] = useState<ContextNode | null>(null);
	const [reloadToken, setReloadToken] = useState(0);
	/** Synthetic "+N" cards the reader has opened. Reset with the graph. */
	const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
	const [expandAll, setExpandAll] = useState(false);
	const [ownScope, setOwnScope] = useState<AnswerScope>("message");
	const [ownFocus, setOwnFocus] = useState<string | null | undefined>(undefined);

	// A chat with no message to narrow to can only be read whole.
	const hasMessage = Boolean(messageId);
	const chosenScope = scope ?? ownScope;
	const activeScope: AnswerScope = hasMessage ? chosenScope : "chat";

	const setScope = useCallback(
		(next: AnswerScope) => {
			if (scope === undefined) setOwnScope(next);
			onScopeChange?.(next);
			setSelected(null);
		},
		[scope, onScopeChange],
	);

	useEffect(() => {
		let cancelled = false;
		setState({ phase: "loading" });
		setSelected(null);
		setExpanded(new Set());

		const request: Promise<ContextGraphResult> =
			activeScope === "chat"
				? contextGraphService.getChatGraph(chatId)
				: contextGraphService.getMessageGraph(chatId, messageId);

		void request.then((result) => {
			if (cancelled) return;
			if (result.status === "ok") setState({ phase: "ready", graph: result.graph });
			else if (result.status === "unavailable") setState({ phase: "unavailable" });
			else setState({ phase: "error" });
		});

		return () => {
			cancelled = true;
		};
	}, [chatId, messageId, activeScope, reloadToken]);

	const retry = useCallback(() => setReloadToken((token) => token + 1), []);

	const toggleExpanded = useCallback((id: string) => {
		setExpanded((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const handleSelect = useCallback(
		(node: ContextNode) => {
			// A "+N" card is a door, not a citation: opening it is the whole
			// interaction, so it never opens the side panel instead.
			if (isCollapsedNode(node)) {
				toggleExpanded(node.id);
				return;
			}
			// Every card opens its panel: a figure shows the value, the quote,
			// the document and the page it came from, with the way into the PDF
			// one button further.
			setSelected(node);
		},
		[toggleExpanded],
	);

	const graph = state.phase === "ready" ? state.graph : null;

	/**
	 * The chosen focus, or — while nobody has chosen — whatever the API
	 * centred the graph on.
	 */
	const chosenFocus = onFocusChange ? focus : ownFocus;
	const activeFocus = chosenFocus !== undefined ? chosenFocus : (graph?.focus ?? null);

	const setFocus = useCallback(
		(next: string | null) => {
			if (!onFocusChange) setOwnFocus(next);
			onFocusChange?.(next);
			setExpanded(new Set());
			setSelected(null);
		},
		[onFocusChange],
	);

	// What is actually drawn: the answer narrowed to one company and folded
	// down to the cards that carry it. The full graph stays untouched, so the
	// company list and a synthetic card's members can both be resolved from it.
	const shown = useMemo(
		() =>
			graph ? collapseGraph(graph, { focus: activeFocus, expanded, expandAll }) : null,
		[graph, activeFocus, expanded, expandAll],
	);

	const companies = useMemo(() => (graph ? companiesIn(graph) : []), [graph]);

	/** The real nodes a selected "+N" card stands in for. */
	const members = useMemo(() => {
		if (!graph || !selected) return [];
		const ids = new Set(collapsedIds(selected));
		return graph.nodes.filter((node) => ids.has(node.id));
	}, [graph, selected]);

	const narrow = useNarrowLayout();

	return (
		// `context-graph` carries the palette's custom properties, so it has to
		// sit above the legend and the detail panel too — not only the canvas.
		// It is idempotent: the page nests this inside its own.
		<div
			ref={narrow.ref}
			data-testid="context-graph-answer-view"
			data-layout={narrow.isNarrow ? "stacked" : "side-by-side"}
			className={`context-graph context-graph-answer-view ${
				narrow.isNarrow
					? "context-graph-answer-view--narrow"
					: "context-graph-answer-view--wide"
			} flex h-full min-h-0 flex-1 flex-col overflow-hidden`}
		>
			<div className="context-graph-answer-view__toolbar">
				<ContextGraphScopeToggle
					scope={activeScope}
					hasMessage={hasMessage}
					onScopeChange={setScope}
				/>
				{state.phase === "ready" && (
					<ContextGraphScopeControls
						companies={companies}
						focus={activeFocus}
						onFocusChange={setFocus}
						expandAll={expandAll}
						onExpandAllChange={setExpandAll}
					/>
				)}
			</div>

			{state.phase === "error" ? (
				<ErrorDisplay error={new Error("context-graph-unreachable")} onRetry={retry} />
			) : state.phase === "loading" ? (
				<div className="h-[320px] flex-1 animate-pulse rounded-lg border border-gray-200 bg-white" />
			) : state.phase === "unavailable" ? (
				<Notice>Graph projection not available yet.</Notice>
			) : shown === null || shown.nodes.length === 0 ? (
				<Notice>No graph for this answer yet.</Notice>
			) : (
				<div className="context-graph-answer-view__surface">
					<div className="context-graph-answer-view__graph">
						<div className="min-h-[320px] flex-1">
							<ContextGraphCanvas
								graph={shown}
								focus={activeFocus}
								onSelectNode={handleSelect}
							/>
						</div>
						<ContextGraphLegend graph={shown} />
					</div>

					{selected && (
						<div className="context-graph-answer-view__detail">
							<ContextGraphNodePanel
								node={selected}
								members={members}
								onOpenHref={onOpenHref}
								onClose={() => setSelected(null)}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

/**
 * How wide the view actually is, from its own box.
 *
 * Starts wide, so the page — which is always wide — never flashes the stacked
 * layout for the frame before the first measurement lands, and so a host
 * without a ResizeObserver at all keeps today's look.
 */
function useNarrowLayout(): {
	ref: React.RefObject<HTMLDivElement | null>;
	isNarrow: boolean;
} {
	const ref = useRef<HTMLDivElement | null>(null);
	const [isNarrow, setIsNarrow] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element || typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const width = entry.contentRect.width;
				// A zero width is a detached or not-yet-laid-out box, not a
				// narrow one: jsdom reports it, and so does a hidden container.
				if (width > 0) setIsNarrow(width < SIDE_BY_SIDE_MIN_WIDTH);
			}
		});
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	return { ref, isNarrow };
}

/** A quiet, non-alarming empty state — none of these are failures. */
const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div className="rounded-lg border border-gray-200 bg-white px-6 py-10 text-center text-sm text-text-muted">
		{children}
	</div>
);

export default ContextGraphAnswerView;
